import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect } from 'react'
import { BackHandler, Platform, ScrollView, StyleSheet, View } from 'react-native'
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated'
import { ensurePushEnabled } from '@/modules/push'
import { SafeAreaView } from 'react-native-safe-area-context'
import Menu from '../../../assets/mainHeader/Menu.png'
import { ActionModal } from '../../components'
import { useCheatSheet } from '../../components/CheatSheet/useCheatSheet'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { useStore } from '../../models'

import styles from './styles'
import playTabConfig from '@/screens/play-screen/playTabConfig'
import { useFocusEffect } from 'expo-router'
import { useIsFocused } from '@react-navigation/native'

import { events } from '../../models/analytics/analytics-events'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { useIsConvexEnabled } from '@/hooks/convex/useIsConvexEnabled'

import { MyFirstGameWelcome } from './Sections/MyFirstGameWelcome'
import { Streaks } from '@/components/Streaks/Streaks'
import { MiniGames } from '@/screens/play-screen/Sections/MiniGames/MiniGames'
import { BeSocial, PlayPoker } from '@/screens/play-screen/Sections/PlayPoker/PlayPoker'
import { DailyPokerish } from './Sections/DailyPokerish'
import { YourConvexGames } from './Sections/YourGames/YourConvexGames'
import { YourGames } from './Sections/YourGames'
import { CommunityGamesSection } from '@/screens/GameContainer/CommunityGamesSection'
import { LearnPoker } from './Sections/LearnPoker'
import { BadgesTeaser } from './Sections/BadgesTeaser'
import { AdvisorPromoBanner } from './Sections/AdvisorPromo'

import { useSessionStore } from '@/models/session/useSessionStore'
import { MyTournaments } from '@/screens/tournament/MyTournaments'
import { useHandleResetForTesting } from '@/screens/profile-screen/edit/AvatarModule'

function TestFirstTimeSignInButton() {
  const store = useStore()
  const handleResetForTesting = useHandleResetForTesting()

  const onPress = async () => {
    await handleResetForTesting()
    await store.session.updateUserInfo({ isUserNew: false, questionAnswers: [] })
  }

  return (
    <DS.Button variant="Medium/Secondary" type="On Dark" onPress={onPress}>
      Simulate First-Time Sign-In
    </DS.Button>
  )
}

export const PlayScreen = observer(() => {
  const isFocused = useIsFocused()
  const { openDrawer } = useDrawerStore()

  const convexConfig = useIsConvexEnabled()
  const appLaunchCount = useSessionStore((state) => state.appLaunchCount)
  const { open: handleOpenCheatSheet } = useCheatSheet()
  const store = useStore()
  const {
    game: { teardown },
    session: { setLastVisitedTab, user, userInfo },
    analytics: { setEvent }
  } = useStore()

  
  useEffect(() => {
    setTimeout(() => {
      console.log('lastTimePermissionExpired', store.permissions.pushPermissionExpired)
      if (store.permissions.pushPermissionExpired) {
        store.permissions.setPushPermissionRequestedAt(Date.now())
        ensurePushEnabled(true, false)
      }
    }, 1500)
  }, [store.permissions.pushPermissionExpired, store.permissions.setPushPermissionRequestedAt])

  const userId = user?._id.toString()

  const [isVisible, setIsVisible] = React.useState(false)

  const fetchUserNotifications = useCallback(async () => {
    try {
      await Promise.allSettled([store.session.updateUserVisitCount(), store.session.getUserNotification()])
    } catch (error) {
      console.log('Error in getUserNotification', error)
    }
  }, [store.session])

  useEffect(() => {
    fetchUserNotifications()
  }, [fetchUserNotifications])

  useEffect(() => {
    if (isFocused) {
      setLastVisitedTab('play')
    }
  }, [isFocused, setLastVisitedTab])

  const updateUserSegmentIdentity = useCallback(async () => {
    try {
      // Note: Update Segment identity if user logged in
      await store.session.getUserInfo()
    } catch (error) {
      console.log('Error in getUserNotification', error)
    }
  }, [store.session])

  useFocusEffect(
    React.useCallback(() => {
      if (!store.analytics.isSkillLevelAvailableInTraits) {
        updateUserSegmentIdentity()
      }
    }, [store.analytics.isSkillLevelAvailableInTraits, updateUserSegmentIdentity])
  )

  useFocusEffect(
    useCallback(() => {
      teardown()
    }, [teardown])
  )

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS !== 'web') return
      if (store.permissions.pushPermissionExpired) {
        store.permissions.setShouldShowWebNotificationPrompt(true)
      }
    }, [store.permissions])
  )

  useFocusEffect(
    useCallback(() => {
      const backAction = () => {
        setIsVisible(true)
        return true
      }
      const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction)
      return () => backHandler.remove()
    }, [])
  )

  //Event for play screen
  useEffect(() => {
    console.log('Play Screen viewed')
    setEvent(events.playscreen.play_screen_viewed, {
      userId
    })
  }, [setEvent, userId])

  const navToAuth = useCallback(() => {
    if (store.session) {
      store.session.logout()
    }
  }, [store.session])

  const showPokerish = playTabConfig.modules.find(m => m.name === 'pokerish')?.enabled
  // Badges renders as a headed section ("Badges") right after "Be Social",
  // before yourGames.
  const showBadges = playTabConfig.modules.find(m => m.name === 'badges')?.enabled
  const showAdvisorPromo = playTabConfig.modules.find(m => m.name === 'advisorPromo')?.enabled
  const showYourConvexGames = (convexConfig.enabled) && (playTabConfig.modules.find(m => m.name === 'yourGames')?.enabled)
  const showYourGames = playTabConfig.modules.find(m => m.name === 'yourGames')?.enabled
  const showCommunityGames = playTabConfig.modules.find(m => m.name === 'communityGames')?.enabled
  const showHandRankings = playTabConfig.modules.find(m => m.name === 'handRankings')?.enabled
  let showGuidedPlaythrough = playTabConfig.modules.find(m => m.name === 'firstGame')?.enabled
  if (userInfo?.myFirstGameFinish){
    showGuidedPlaythrough = false;
  }
  if (appLaunchCount > 10){
    showGuidedPlaythrough = false;
  }


  return (
    <Animated.View entering={FadeIn} style={{ flex: 1 }}>
      <SafeAreaView style={[styles.container]} edges={['top', 'left', 'right']}>
        {isFocused ? <StatusBar style={'dark' as StatusBarStyle} /> : null}
        <MainHeader hideLeftDefaultIcon rightIcon={Menu} rightPress={() => openDrawer()} />
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: 24
          }}
          nestedScrollEnabled
        >
          <Animated.View
            entering={FadeInDown}
            style={{
              flex: 1,
              paddingVertical: DS.spacing[6],
              gap: 24
            }}
          >
            {/* first game onboarding (only for new players); the streak section was restored above the redesigned sections */}
            {/* test-only: simulates a first-time sign-in */}
            {/* <View style={[containerStyles.webWrapper, { flexDirection: 'row', justifyContent: 'center', gap: 8 }]}>
              <TestFirstTimeSignInButton />
            </View> */}
            {showGuidedPlaythrough &&
            <View style={containerStyles.webWrapper}>
              <MyFirstGameWelcome compacted />
            </View>
            }

            {/* my streak */}
            <View
              style={[
                containerStyles.webWrapper,
                { flexDirection: 'row', alignItems: 'center', gap: 12 }
              ]}
            >
              <DS.Text variant="Caption/Medium" uppercase>
                MY STREAK
              </DS.Text>
              {/* chips spread evenly across the remaining width next to the heading */}
              <View style={{ flex: 1 }}>
                <Streaks compact />
              </View>
            </View>

            {/* advisor feature banner: an ad strip, deliberately unlike the
                nav row cards below (full width, half a row's height) */}
            {showAdvisorPromo &&
            <View style={containerStyles.webWrapper}>
              <AdvisorPromoBanner />
            </View>
            }

            {/* challenges, play poker, be social and badges share one rhythm; section dividers were dropped for a cleaner look */}
            <View style={[containerStyles.webWrapper, { gap: DS.spacing[32] }]}>
              {/* minigames, "Today's Challenges" */}
              <MiniGames />

              {/* play poker */}
              <PlayPoker />

              {/* be social */}
              <BeSocial />

              {/* badges */}
              {showBadges && <BadgesTeaser />}
            </View>

            {/* pokerish */}
            {showPokerish &&
            <View style={containerStyles.webWrapper}>
              <DailyPokerish />
            </View>
            }
            <View style={[containerStyles.webWrapper, { gap: DS.spacing[32] }]}>
              {/* your convex games, "Your Games" */}
              {showYourConvexGames && <YourConvexGames />}

              {/* your games, "Your Scheduled Games" */}
              {showYourGames && <YourGames />}

              {/* community games */}
              {showCommunityGames && <CommunityGamesSection />}

              {/* your tournaments (MTT) */}
              {showYourConvexGames && <MyTournaments />}
            </View>

            {/*hand rankings */}
            {showHandRankings &&
            <View style={containerStyles.webWrapper}>
              <LearnPoker handleOpenCheatSheet={handleOpenCheatSheet} />
            </View>
            }

            <ActionModal
              key={'logout'}
              onAction={navToAuth}
              onModalHide={() => setIsVisible(false)}
              onClose={() => setIsVisible(false)}
              message={translate('common.logoutConfirmation')}
              visible={isVisible}
            />

          </Animated.View>
        </ScrollView>
      </SafeAreaView>
    </Animated.View>
  )
})

const containerStyles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  },
  webWrapperExtended: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center'
  }
})
